// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {FeedCategoryResponse} from '@fluxer/schema/src/domains/feed/FeedCategorySchemas';
import {makeAutoObservable} from 'mobx';

// The list changes only when the admins edit it; a feed page opened later than this asks again.
export const FEED_CATEGORIES_STALE_MS = 10 * 60 * 1000;

export interface FeedCategory {
	readonly id: string;
	readonly name: string;
	readonly emoji: string;
}

// Micup akis-kategori: the categories of the caller's country (the server picks them by the request, at most 50), in
// the admins' order. Plain state; FeedCategoryCommands.loadCategories asks the server.
class FeedCategories {
	categories: ReadonlyArray<FeedCategory> = [];
	fetchedAt: number | null = null;
	isLoading = false;
	failure: 'forbidden' | 'error' | null = null;
	// Bumped on reset, so an answer for the previous account is dropped.
	private session = 0;

	constructor() {
		makeAutoObservable<FeedCategories, 'session'>(this, {session: false}, {autoBind: true});
	}

	reset(): void {
		this.session++;
		this.categories = [];
		this.fetchedAt = null;
		this.isLoading = false;
		this.failure = null;
	}

	// The session a request belongs to, or null when none should start (one in flight, or the list is fresh).
	beginLoad(force: boolean): number | null {
		if (this.isLoading) return null;
		if (!force && this.fetchedAt !== null && Date.now() - this.fetchedAt <= FEED_CATEGORIES_STALE_MS) return null;
		this.isLoading = true;
		return this.session;
	}

	handleLoaded(session: number, list: ReadonlyArray<FeedCategoryResponse>): void {
		if (session !== this.session) return;
		this.categories = list.map((category) => ({id: category.id, name: category.name, emoji: category.emoji}));
		this.fetchedAt = Date.now();
		this.isLoading = false;
		this.failure = null;
	}

	// No list is kept and the next opening asks again.
	handleLoadFailed(session: number, failure: 'forbidden' | 'error'): void {
		if (session !== this.session) return;
		this.categories = [];
		this.fetchedAt = null;
		this.isLoading = false;
		this.failure = failure;
	}

	get(categoryId: string): FeedCategory | null {
		return this.categories.find((category) => category.id === categoryId) ?? null;
	}
}

const feedCategories = new FeedCategories();
ResettableStates.register(feedCategories, feedCategories.reset);
AccountScopedWork.registerCancellation(() => feedCategories.reset());

export default feedCategories;
